import { Component, inject, input } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { lastValueFrom } from 'rxjs';
import { TabComponent } from '../../../../components/tab/tab.component';
import { TabLayoutComponent } from '../../../../components/tab-layout/tab-layout.component';
import { LineChartComponent } from '../../../../components/line-chart/line-chart.component';
import { SelectComponent } from '../../../../components/select/select.component';
import { ConfigService } from '../../../../services/config.service';
import { LineChartData } from '../../../../interfaces/global/chart';
import { environment } from '../../../../../environments/environment';

interface VisitsPerDayRow {
  Visits: number;
  Date: string;
}

interface VisitsPerDayResponse {
  success: boolean;
  data: {
    rows: VisitsPerDayRow[];
    filters?: {
      Product?: string[];
    };
  };
}

@Component({
  selector: 'app-visits-performance-tab',
  imports: [
    CommonModule,
    TabLayoutComponent,
    LineChartComponent,
    SelectComponent,
  ],
  templateUrl: './visits-performance-tab.component.html',
  styleUrl: './visits-performance-tab.component.scss',
  providers: [
    { provide: TabComponent, useExisting: VisitsPerformanceTabComponent },
  ],
})
export class VisitsPerformanceTabComponent extends TabComponent {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);

  override name = this.dictionaryService.get('VisitsPerformance');

  userId = input.required<string>();

  chartData: LineChartData = { labels: [], datasets: [] };
  chartLoading = false;
  productOptions: string[] = [];
  selectedProducts: string[] = [];
  private filtersLoaded = false;

  public async main(): Promise<void> {
    await this.fetchData();
  }

  private async fetchData(): Promise<void> {
    this.chartLoading = true;
    const { from, to } = this.configService.getApiDateTimeRange();

    const body: Record<string, unknown> = {
      from,
      to,
      returnAll: true,
      getTotalCount: 1,
      getAvailableFilters: this.filtersLoaded ? 0 : 1,
      filters:
        this.selectedProducts.length > 0
          ? { Product: this.selectedProducts }
          : {},
    };

    try {
      const response = await lastValueFrom(
        this.http.post<VisitsPerDayResponse>(
          `${environment.apiBaseUrl}/visits/tables/visits-per-day/${this.userId()}`,
          body,
        ),
      );
      if (response.success) {
        this.buildChartData(response.data.rows);
        this.filtersLoaded = true;

        if (response.data.filters?.Product) {
          this.productOptions = response.data.filters.Product;
        }
      }
    } catch {
      this.error = 'Failed to load data';
    } finally {
      this.chartLoading = false;
    }
  }

  private buildChartData(rows: VisitsPerDayRow[]): void {
    const sortedRows = [...rows].sort(
      (a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime(),
    );

    this.chartData = {
      labels: sortedRows.map((r) => this.formatDisplayDate(r.Date)),
      datasets: [
        {
          label: this.dictionaryService.get('Visits'),
          values: sortedRows.map((r) => r.Visits),
        },
      ],
    };
  }

  onProductFilterChange(items: string[]): void {
    this.selectedProducts = items;
    this.fetchData();
  }

  private formatDisplayDate(dateStr: string): string {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }
}
